import React, { ReactNode, useEffect } from 'react';
import { Collapse } from '@duxton-web/ui';

import RolloutTitle from './header';
import {
  getProperlyInstrumentedFacetList,
  useVariableStatsQuery,
} from '@/common/ConstraintsEditor/Constraint';
import { validateValue } from '@/common/StaticValueInput/ajv';
import { useVariable } from '@/contexts/VariableContext';
import { RolloutTarget } from '@/lib/PlannerDTOs';
import RolloutBlock, {
  isSliderConstraint,
} from '@/pages/RolloutTab/RolloutEditor/RolloutBlock';
import RolloutDiffBlockV3 from '@/pages/RolloutTab/RolloutEditor/RolloutDiffBlock/RolloutDiffBlockV3';
import { customCollapseIcon } from '@/pages/RolloutTab/RolloutEditor/RolloutEditorV2';

import './index.css';

interface TargetProps {
  rolloutTarget: RolloutTarget;
  index: number;
  targetIndexToHighlight: number;
  handleDeleteTarget: (index: number) => void;
  changeOrder: (currentIndex: number, newIndex: number) => void;
  enableReorder: boolean;
  disabled: boolean;
  isReordered: (rolloutTarget: RolloutTarget) => boolean;
  activePanels: string[];
  handlePanelToggle: (key: string) => void;
  maxPosition: number;
  isDiff: boolean;
  handleTargetChange: (
    targetIndex: number,
    rolloutTarget: RolloutTarget,
  ) => void;
  assignBy: string;
}

export default function Target({
  rolloutTarget,
  index,
  targetIndexToHighlight,
  handleDeleteTarget,
  changeOrder,
  enableReorder,
  disabled,
  isReordered,
  activePanels,
  handlePanelToggle,
  maxPosition,
  isDiff = false,
  handleTargetChange,
  assignBy,
}: TargetProps) {
  const variable = useVariable();

  useEffect(() => {
    if (targetIndexToHighlight === index)
      document.getElementById(`rollot-editor-target-${index}`).focus();
  }, [targetIndexToHighlight]);

  // Add null check for variable before using it in hooks
  const variableStats = useVariableStatsQuery(
    variable?.name ? [variable.name] : [],
  );
  const properlyInstrumentedFacetList = getProperlyInstrumentedFacetList(
    variableStats,
  ) as string[];

  function renderRolloutTargetContent(
    index: number,
    rolloutTarget: RolloutTarget,
  ): ReactNode {
    if (isDiff) return <RolloutDiffBlockV3 target={rolloutTarget} />;

    return (
      <RolloutBlock
        rolloutSlice={rolloutTarget}
        targetIndex={index}
        variable={variable}
        disabled={disabled || enableReorder}
        onChange={(target: RolloutTarget) => handleTargetChange(index, target)}
        assignBy={assignBy}
      />
    );
  }

  const isValid = !validateValue(variable, {
    value: rolloutTarget.value,
    string: rolloutTarget.string,
    script: rolloutTarget.script,
  })?.length;

  let inproperFacets = false;
  if (rolloutTarget?.constraints?.length)
    rolloutTarget?.constraints?.forEach((constraint) => {
      if (
        !isSliderConstraint(variable, assignBy, constraint) &&
        constraint?.target &&
        !properlyInstrumentedFacetList.includes(constraint?.target)
      ) {
        inproperFacets = true;
        return false;
      }
    });

  return (
    <div
      key={`rollout-target-${index}`}
      tabIndex={-1}
      id={`rollot-editor-target-${index}`}
      className={`feature-rollout-rollout-target ${
        targetIndexToHighlight === index
          ? isValid && !inproperFacets
            ? `blink-bg`
            : 'blink-bg-no-slotting'
          : ``
      }`}
    >
      <Collapse
        size="small"
        activeKey={activePanels}
        onChange={() => handlePanelToggle(String(index))}
        style={{
          borderRadius: 0,
          border: 'none',
          background: 'transparent',
        }}
        className={`rollout-editor-target ${
          isValid && !inproperFacets
            ? ''
            : !isValid
              ? 'invalid-rollout-target'
              : 'inproper-rollout-target'
        }`}
        expandIcon={customCollapseIcon}
        items={[
          {
            key: index,
            label: (
              <RolloutTitle
                rolloutTarget={rolloutTarget}
                showReorderButtons={enableReorder}
                rolloutIndex={index}
                disabled={disabled}
                deleteBlock={() => handleDeleteTarget(index)}
                onReorderUpClick={() => changeOrder(index, index - 1)}
                onReorderDownClick={() => changeOrder(index, index + 1)}
                onRolloutTargetIndexChange={(fromIndex, toIndex) => {
                  changeOrder(fromIndex - 1, toIndex - 1);
                }}
                isReordered={isReordered}
                maxPosition={maxPosition}
              />
            ),
            children: renderRolloutTargetContent(index, rolloutTarget),
          },
        ]}
      />
    </div>
  );
}
